/**
 * Impor Log Mesin Absensi — Modul Kehadiran Tahap 6 (2026-10-14, user request).
 *
 * Brand-agnostic: HR mengunggah CSV/XLSX dari mesin/software absensi apa pun,
 * memetakan kolom secara dinamis (profil tersimpan per sumber untuk dipakai
 * ulang), lalu pratinjau validasi sebelum konfirmasi.
 * Aturan keras:
 *  - ID person mesin TIDAK unik global — dilingkupkan per perangkat (scope).
 *  - Log mesin = bukti pendukung; TIDAK pernah menimpa absen website.
 *    Konflik → Antrean Review, diselesaikan HR satu per satu
 *    (pakai data mesin ATAU pertahankan website).
 *  - Tanpa kolom tipe: strategi derivasi first_in_last_out (tap pertama =
 *    masuk, tap terakhir = pulang) atau alternate — dipilih eksplisit.
 * Sub-tab: Impor Baru | Antrean Review | Riwayat Impor | Perangkat & Sumber | Pemetaan ID.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Plus, Trash2, Pencil,
  Save, XCircle, Loader2, RefreshCw, ShieldAlert, Link2, History,
} from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SearchableSelect from "@/components/SearchableSelect";

const DATE_FORMATS = ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", "MM/DD/YYYY"];
const AUTH_TECH = [
  { value: "fingerprint", label: "Sidik Jari" },
  { value: "wajah", label: "Wajah" },
  { value: "kartu", label: "Kartu" },
  { value: "pin", label: "PIN" },
  { value: "lainnya", label: "Lainnya" },
];
const EMPTY_MAP = {
  person_col: "", datetime_col: "", date_col: "", time_col: "", type_col: "",
  device_col: "", event_id_col: "", date_format: "YYYY-MM-DD", derivation: "first_in_last_out",
};

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
const TYPE_LABEL = { in: "Masuk", out: "Pulang", unknown: "Tidak diketahui" };

function Th({ children, right }) {
  return <th className={`sticky top-0 bg-slate-50 px-3 py-2 ${right ? "text-right" : "text-left"}`}>{children}</th>;
}
function EmptyRow({ cols, text }) {
  return <tr><td colSpan={cols} className="px-3 py-6 text-center text-slate-400 italic">{text}</td></tr>;
}

/* ================= Wizard Impor ================= */
function WizardPanel({ sources, devices, canWrite, onChanged }) {
  const [file, setFile] = useState(null);
  const [sourceId, setSourceId] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [hasHeader, setHasHeader] = useState(true);
  const [sheet, setSheet] = useState("");
  const [up, setUp] = useState(null);           // respons upload
  const [map, setMap] = useState(EMPTY_MAP);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [saveProfile, setSaveProfile] = useState(true);
  const [busy, setBusy] = useState(false);

  const src = sources.find((s) => s.id === sourceId);
  const devOptions = useMemo(() => {
    if (!src) return [];
    const ids = src.device_ids || [];
    return devices.filter((d) => ids.includes(d.id));
  }, [src, devices]);

  const colOptions = useMemo(() => {
    if (!up) return [];
    const sample = up.sample_rows?.[0] || [];
    return up.columns.map((c, i) => ({
      value: String(i),
      label: `${i + 1}. ${c}${sample[i] ? ` — contoh: ${String(sample[i]).slice(0, 24)}` : ""}`,
    }));
  }, [up]);

  const doUpload = async (sheetOverride) => {
    if (!file || !sourceId) return toast.error("Pilih sumber dan file terlebih dahulu.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_id", sourceId);
      if (deviceId) fd.append("device_id", deviceId);
      fd.append("has_header", String(hasHeader));
      const sh = sheetOverride ?? sheet;
      if (sh) fd.append("sheet", sh);
      const r = await api.post("/attendance/import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUp(r.data);
      setPreview(null);
      setResult(null);
      const sp = r.data.saved_profile;
      if (sp) {
        setMap({ ...EMPTY_MAP, ...Object.fromEntries(Object.entries(EMPTY_MAP).map(([k]) => [k, sp[k] == null ? EMPTY_MAP[k] : String(sp[k])])), date_format: sp.date_format || "YYYY-MM-DD", derivation: sp.derivation || "first_in_last_out" });
        toast.success("Profil pemetaan tersimpan dimuat otomatis.");
      }
      toast.success(`File terbaca: ${r.data.rows_total} baris data.`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toInt = (v) => (v === "" || v == null ? null : parseInt(v, 10));
  const buildMapping = () => ({
    person_col: toInt(map.person_col),
    datetime_col: toInt(map.datetime_col),
    date_col: toInt(map.date_col),
    time_col: toInt(map.time_col),
    type_col: toInt(map.type_col),
    device_col: toInt(map.device_col),
    event_id_col: toInt(map.event_id_col),
    date_format: map.date_format,
    derivation: map.type_col === "" ? map.derivation : null,
  });
  const mapValid = map.person_col !== "" && (map.datetime_col !== "" || map.date_col !== "");

  const doPreview = async () => {
    if (!mapValid) return toast.error("Kolom ID Person dan Tanggal/Waktu wajib dipetakan.");
    setBusy(true);
    try {
      const r = await api.post(`/attendance/import/${up.batch_id}/preview`, buildMapping());
      setPreview(r.data);
      if (r.data.parse_failures?.length) toast.warning(`${r.data.parse_failures.length} baris gagal diparse — periksa format tanggal/jam.`);
      else toast.success(`Pratinjau: ${r.data.mapped} baris terpetakan, ${r.data.unmapped} ID tak dikenal.`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); setPreview(null); }
    finally { setBusy(false); }
  };

  const doConfirm = async () => {
    if (!window.confirm(`Konfirmasi impor ${up.rows_total} baris? Log yang bentrok dengan absen website masuk Antrean Review dan TIDAK menimpa data website.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/attendance/import/${up.batch_id}/confirm?save_profile=${saveProfile}`, buildMapping());
      setResult(r.data);
      toast.success(`Impor selesai: ${r.data.inserted} masuk, ${r.data.duplicated} duplikat, ${r.data.failed} gagal.`);
      onChanged?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setUp(null); setPreview(null); setResult(null); setMap(EMPTY_MAP); setSheet(""); };

  const ColSelect = ({ label, field, required }) => (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}{required && " *"}</div>
      <SearchableSelect value={map[field]} onChange={(v) => { setMap({ ...map, [field]: v }); setPreview(null); }}
        options={[{ value: "", label: "— Tidak dipakai —" }, ...colOptions]}
        placeholder="Pilih kolom…" searchPlaceholder="Cari kolom…" emptyText="Kolom tidak ditemukan"
        testid={`att-imp-map-${field}`} />
    </div>
  );

  if (!canWrite) {
    return <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" data-testid="att-imp-readonly">
      Anda hanya memiliki akses lihat. Permission <code className="bg-white px-1">attendance.create</code> diperlukan untuk mengimpor log mesin.
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="att-imp-wizard">
      {/* Langkah 1: Unggah */}
      <div className="border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center bg-[#0B1D3A] text-white text-[10px] font-bold">1</span>
          <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Unggah File Log (CSV / XLSX)</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Sumber Data *</div>
            <SearchableSelect value={sourceId} onChange={(v) => { setSourceId(v); setDeviceId(""); }}
              options={sources.filter((s) => s.is_active).map((s) => ({ value: s.id, label: `${s.name} (${s.source_type === "device_file" ? "File Mesin" : "File Software"})` }))}
              placeholder="Pilih sumber…" searchPlaceholder="Cari sumber…" emptyText="Belum ada sumber — buat di tab Perangkat & Sumber" testid="att-imp-source" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Perangkat (ruang lingkup ID)</div>
            <SearchableSelect value={deviceId} onChange={setDeviceId}
              options={devOptions.map((d) => ({ value: d.id, label: `${d.name} — ${d.device_code}` }))}
              placeholder={src?.scope === "device" ? "Pilih perangkat…" : "Opsional"}
              searchPlaceholder="Cari perangkat…" emptyText="Tidak ada perangkat pada sumber ini" testid="att-imp-device" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">File *</div>
            <input type="file" accept=".csv,.txt,.xlsx,.xlsm" data-testid="att-imp-file"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setUp(null); setPreview(null); setResult(null); }}
              className="block w-full text-[11px] file:mr-2 file:px-3 file:py-1.5 file:border-0 file:bg-[#0B1D3A] file:text-white file:text-[10px] file:uppercase file:tracking-wider hover:file:bg-[#16305c] border border-slate-200" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-[11px]" data-testid="att-imp-has-header">
            <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
            Baris pertama adalah header kolom
          </label>
          {up && up.sheets?.length > 1 && (
            <div className="flex items-center gap-2">
              <SearchableSelect value={sheet || up.sheets[0]} onChange={setSheet}
                options={up.sheets.map((s) => ({ value: s, label: s }))}
                placeholder="Sheet…" testid="att-imp-sheet" />
              <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => doUpload(sheet)} disabled={busy} data-testid="att-imp-sheet-reload">
                <RefreshCw size={10} /> Muat Sheet Ini
              </Button>
            </div>
          )}
          <Button size="sm" onClick={() => doUpload()} disabled={busy || !file || !sourceId} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" data-testid="att-imp-upload-btn">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Baca File
          </Button>
          {up && <Button size="sm" variant="outline" className="rounded-none" onClick={reset} data-testid="att-imp-reset"><XCircle size={12} /> Mulai Ulang</Button>}
        </div>
        {up && (
          <div className="text-[11px] text-slate-600 border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="att-imp-upload-info">
            <span className="font-semibold text-emerald-800">{up.filename || "File"}</span> terbaca — {up.rows_total} baris data, zona waktu {up.timezone}.
            Kolom terdeteksi: <span className="font-mono text-[10px]">{up.columns.join(" | ")}</span>
          </div>
        )}
      </div>

      {/* Langkah 2: Pemetaan kolom */}
      {up && !result && (
        <div className="border border-slate-200 bg-white p-4 space-y-3" data-testid="att-imp-mapping">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center bg-[#0B1D3A] text-white text-[10px] font-bold">2</span>
            <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Pemetaan Kolom</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <ColSelect label="ID Person (karyawan di mesin)" field="person_col" required />
            <ColSelect label="Tanggal + Jam (gabungan)" field="datetime_col" />
            <ColSelect label="Tanggal (terpisah)" field="date_col" />
            <ColSelect label="Jam (terpisah)" field="time_col" />
            <ColSelect label="Tipe Kejadian (masuk/pulang)" field="type_col" />
            <ColSelect label="ID Perangkat (kolom)" field="device_col" />
            <ColSelect label="ID Kejadian (anti-duplikat mesin)" field="event_id_col" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Format Tanggal *</div>
              <SearchableSelect value={map.date_format} onChange={(v) => { setMap({ ...map, date_format: v }); setPreview(null); }}
                options={DATE_FORMATS.map((f) => ({ value: f, label: f }))} placeholder="Format…" testid="att-imp-map-date-format" />
            </div>
            {map.type_col === "" && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Strategi Derivasi Tipe *</div>
                <SearchableSelect value={map.derivation} onChange={(v) => { setMap({ ...map, derivation: v }); setPreview(null); }}
                  options={[
                    { value: "first_in_last_out", label: "Tap pertama = Masuk, tap terakhir = Pulang" },
                    { value: "alternate", label: "Bergantian: ganjil = Masuk, genap = Pulang" },
                  ]}
                  placeholder="Strategi…" testid="att-imp-map-derivation" />
              </div>
            )}
          </div>
          <div className="text-[10px] text-slate-500 italic">
            Tanpa kolom tipe, sistem menurunkan masuk/pulang sesuai strategi yang dipilih — tap tengah ditandai &quot;tidak diketahui&quot;.
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={doPreview} disabled={busy || !mapValid} className="rounded-none bg-[#0B1D3A]" data-testid="att-imp-preview-btn">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Pratinjau Validasi
            </Button>
          </div>
        </div>
      )}

      {/* Langkah 3: Pratinjau + konfirmasi */}
      {preview && !result && (
        <div className="border border-slate-200 bg-white p-4 space-y-3" data-testid="att-imp-preview">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-[#0B1D3A] text-white text-[10px] font-bold">3</span>
              <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Pratinjau (20 baris pertama)</h3>
            </div>
            <div className="text-[10px] uppercase tracking-wider">
              <span className="text-emerald-700 font-semibold">{preview.mapped} terpetakan</span>
              <span className="mx-2 text-slate-300">|</span>
              <span className={preview.unmapped ? "text-rose-700 font-semibold" : "text-slate-500"}>{preview.unmapped} ID tak dikenal</span>
              <span className="mx-2 text-slate-300">|</span>
              <span className={preview.parse_failures?.length ? "text-rose-700 font-semibold" : "text-slate-500"}>{preview.parse_failures?.length || 0} gagal parse</span>
            </div>
          </div>
          <div className="border border-slate-200 overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr>
                <Th>Baris</Th><Th>ID Person</Th><Th>Waktu Kejadian</Th><Th>Tipe</Th><Th>Karyawan</Th>
              </tr></thead>
              <tbody>
                {preview.preview.map((p) => (
                  <tr key={p.row_no} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-imp-pv-row-${p.row_no}`}>
                    <td className="px-3 py-1.5 tabular-nums text-slate-500">{p.row_no}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px]">{p.external_person_id}</td>
                    <td className="px-3 py-1.5 tabular-nums">{fmtDT(p.event_time)}</td>
                    <td className="px-3 py-1.5 text-[10px] uppercase tracking-wider">
                      {p.event_type === "in" && <span className="text-emerald-700 font-semibold">Masuk{p.derived ? " (turunan)" : ""}</span>}
                      {p.event_type === "out" && <span className="text-sky-700 font-semibold">Pulang{p.derived ? " (turunan)" : ""}</span>}
                      {(!p.event_type || p.event_type === "unknown") && <span className="text-slate-400">Tidak diketahui</span>}
                    </td>
                    <td className="px-3 py-1.5">{p.user_name || <span className="text-rose-700 font-semibold">Tidak dikenal — dilewati</span>}</td>
                  </tr>
                ))}
                {(preview.parse_failures || []).map((f) => (
                  <tr key={`f-${f.row_no}`} className="border-t border-rose-100 bg-rose-50/50" data-testid={`att-imp-pv-fail-${f.row_no}`}>
                    <td className="px-3 py-1.5 tabular-nums text-slate-500">{f.row_no}</td>
                    <td colSpan={4} className="px-3 py-1.5 text-rose-700 text-[10px]">
                      Gagal diparse: {f.reason} — <span className="font-mono">{(f.raw || []).join(" | ")}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.unmapped > 0 && (
            <div className="border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 flex items-start gap-2" data-testid="att-imp-unmapped-warn">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{preview.unmapped} baris memakai ID yang belum dipetakan ke karyawan. Baris tersebut <b>dilewati</b> (tidak membuat karyawan baru). Petakan dulu di tab <b>Pemetaan ID</b> bila perlu.</span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-[11px]" data-testid="att-imp-save-profile">
              <input type="checkbox" checked={saveProfile} onChange={(e) => setSaveProfile(e.target.checked)} />
              Simpan pemetaan sebagai profil sumber ini (otomatis terisi pada impor berikutnya)
            </label>
            <Button size="sm" onClick={doConfirm} disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" data-testid="att-imp-confirm-btn">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Konfirmasi Impor {up.rows_total} Baris
            </Button>
          </div>
        </div>
      )}

      {/* Hasil */}
      {result && (
        <div className="border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="att-imp-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-700" />
            <h3 className="font-display text-[13px] uppercase tracking-wider text-emerald-900">Impor Selesai</h3>
          </div>
          <div className="text-[11px] text-emerald-900">
            <b>{result.inserted}</b> kejadian masuk · <b>{result.duplicated}</b> duplikat dilewati · <b>{result.failed}</b> baris gagal.
            Konflik dengan absen website (bila ada) menunggu di tab <b>Antrean Review</b> — data website tidak ditimpa.
          </div>
          {result.failed_rows?.length > 0 && (
            <div className="border border-rose-200 bg-white max-h-40 overflow-y-auto">
              <table className="w-full text-[10px]">
                <thead className="bg-rose-50 uppercase tracking-wider text-rose-700"><tr><Th>Baris</Th><Th>Alasan</Th></tr></thead>
                <tbody>
                  {result.failed_rows.map((f) => (
                    <tr key={f.row_no} className="border-t border-rose-100" data-testid={`att-imp-fail-${f.row_no}`}>
                      <td className="px-3 py-1 tabular-nums">{f.row_no}</td>
                      <td className="px-3 py-1 text-rose-700">{f.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Button size="sm" variant="outline" className="rounded-none" onClick={reset} data-testid="att-imp-again"><Plus size={12} /> Impor File Lain</Button>
        </div>
      )}
    </div>
  );
}

/* ================= Antrean Review ================= */
function ReviewQueuePanel({ canReview, reloadKey, onChanged }) {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(() => {
    api.get("/attendance/import/review-queue").then((r) => setRows(r.data || [])).catch((e) => {
      if (e.response?.status === 403) setRows([]);
      else toast.error(formatDetail(e.response?.data?.detail));
    });
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!canReview) return null;

  const resolve = async (ev, action) => {
    const msg = action === "use_machine"
      ? `Pakai data MESIN untuk ${ev.user_name || ev.external_person_id} (${fmtDT(ev.event_time)})?\nCatatan website pada tanggal ini akan dibuat/diisi dari log mesin dan ditandai perlu peninjauan bukti.`
      : `Pertahankan data WEBSITE dan abaikan log mesin ini?`;
    if (!window.confirm(msg)) return;
    setBusyId(ev.id);
    try {
      await api.post(`/attendance/import/review-queue/${ev.id}/resolve`, { action });
      toast.success(action === "use_machine" ? "Data mesin diterapkan ke catatan website." : "Log mesin diabaikan — data website dipertahankan.");
      load();
      onChanged?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-3" data-testid="att-imp-review">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A] flex items-center gap-2">
          <ShieldAlert size={14} className="text-amber-600" /> Antrean Review Konflik
        </h3>
        <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={load} data-testid="att-imp-review-reload"><RefreshCw size={10} /> Muat Ulang</Button>
      </div>
      <div className="text-[10px] text-slate-500 italic">
        Log mesin yang bentrok dengan absen website tidak pernah menimpa data asli. Selesaikan satu per satu: pakai data mesin atau pertahankan website.
      </div>
      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr>
            <Th>Karyawan</Th><Th>Waktu Mesin</Th><Th>Tipe</Th><Th>Sumber / Perangkat</Th><Th>Alasan Konflik</Th><Th right>Aksi</Th>
          </tr></thead>
          <tbody>
            {rows === null && <EmptyRow cols={6} text="Memuat antrean…" />}
            {rows?.length === 0 && <EmptyRow cols={6} text="Tidak ada konflik menunggu peninjauan." />}
            {rows?.map((ev) => (
              <tr key={ev.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-imp-review-row-${ev.id}`}>
                <td className="px-3 py-2 font-semibold">{ev.user_name || "—"}<div className="text-[9px] font-mono text-slate-400">ID mesin: {ev.external_person_id}</div></td>
                <td className="px-3 py-2 tabular-nums">{fmtDT(ev.event_time)}</td>
                <td className="px-3 py-2 text-[10px] uppercase tracking-wider">
                  {ev.event_type === "in" ? <span className="text-emerald-700 font-semibold">Masuk</span>
                    : ev.event_type === "out" ? <span className="text-sky-700 font-semibold">Pulang</span>
                    : <span className="text-slate-400">?</span>}
                </td>
                <td className="px-3 py-2 text-[10px]">{ev.source_id?.slice(0, 8)}…{ev.device_id ? ` / ${ev.device_id.slice(0, 8)}…` : ""}</td>
                <td className="px-3 py-2 text-[10px] text-amber-800">{ev.conflict_reason}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                  <Button size="sm" className="rounded-none h-6 text-[9px] bg-[#0B1D3A]" disabled={busyId === ev.id}
                    onClick={() => resolve(ev, "use_machine")} data-testid={`att-imp-resolve-use-${ev.id}`}>
                    {busyId === ev.id ? <Loader2 size={9} className="animate-spin" /> : null} Pakai Mesin
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-6 text-[9px]" disabled={busyId === ev.id}
                    onClick={() => resolve(ev, "keep_website")} data-testid={`att-imp-resolve-keep-${ev.id}`}>
                    Pertahankan Website
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Riwayat Impor ================= */
function BatchHistoryPanel({ reloadKey }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/attendance/import/batches").then((r) => setRows(r.data || [])).catch((e) => toast.error(formatDetail(e.response?.data?.detail)));
  }, [reloadKey]);
  return (
    <div className="space-y-3" data-testid="att-imp-history">
      <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A] flex items-center gap-2"><History size={14} /> Riwayat Batch Impor</h3>
      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr>
            <Th>Waktu Unggah</Th><Th>File</Th><Th>Sumber</Th><Th>Perangkat</Th><Th>Status</Th><Th right>Baris (OK/Dup/Gagal)</Th><Th>Oleh</Th>
          </tr></thead>
          <tbody>
            {rows === null && <EmptyRow cols={7} text="Memuat riwayat…" />}
            {rows?.length === 0 && <EmptyRow cols={7} text="Belum ada impor." />}
            {rows?.map((b) => (
              <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-imp-batch-row-${b.id}`}>
                <td className="px-3 py-2 tabular-nums">{fmtDT(b.created_at)}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{b.filename}</td>
                <td className="px-3 py-2">{b.source_name}</td>
                <td className="px-3 py-2">{b.device_name || <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2 text-[10px] uppercase tracking-wider">
                  {b.status === "confirmed" ? <span className="text-emerald-700 font-semibold">Terkonfirmasi</span> : <span className="text-amber-700 font-semibold">Terunggah</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {b.status === "confirmed" ? `${b.rows_ok ?? 0} / ${b.rows_duplicate ?? 0} / ${b.rows_failed ?? 0}` : `${b.rows_total} baris`}
                </td>
                <td className="px-3 py-2 text-[10px]">{b.uploaded_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Perangkat & Sumber ================= */
function DevicesSourcesPanel({ canWrite, sources, devices, locations, reload }) {
  const [editDev, setEditDev] = useState(null);
  const [editSrc, setEditSrc] = useState(null);
  const emptyDev = { name: "", device_code: "", brand: "", model: "", location_id: "", timezone: "Asia/Jakarta", auth_technology: "fingerprint", is_active: true };
  const emptySrc = { name: "", source_type: "device_file", scope: "device", device_ids: [], timezone: "", is_active: true };

  const saveDev = async () => {
    try {
      const p = { ...editDev, brand: editDev.brand || null, model: editDev.model || null, location_id: editDev.location_id || null };
      if (editDev.id) await api.patch(`/attendance/devices/${editDev.id}`, p);
      else await api.post("/attendance/devices", p);
      toast.success("Perangkat tersimpan."); setEditDev(null); reload();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const delDev = async (id) => {
    if (!window.confirm("Nonaktifkan perangkat ini? Histori pemetaan & log tetap tersimpan.")) return;
    try { await api.delete(`/attendance/devices/${id}`); toast.success("Perangkat dinonaktifkan."); reload(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const saveSrc = async () => {
    try {
      const p = { ...editSrc, timezone: editSrc.timezone || null };
      if (editSrc.id) await api.patch(`/attendance/sources/${editSrc.id}`, p);
      else await api.post("/attendance/sources", p);
      toast.success("Sumber tersimpan."); setEditSrc(null); reload();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="att-imp-devsrc">
      {/* Perangkat */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Perangkat Absensi</h3>
          {canWrite && <Button size="sm" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" onClick={() => setEditDev(emptyDev)} data-testid="att-imp-dev-add"><Plus size={14} /> Tambah Perangkat</Button>}
        </div>
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr>
              <Th>Nama</Th><Th>ID Perangkat</Th><Th>Merk / Model</Th><Th>Teknologi</Th><Th>Zona Waktu</Th><Th>Status</Th><Th right>Aksi</Th>
            </tr></thead>
            <tbody>
              {devices.length === 0 && <EmptyRow cols={7} text="Belum ada perangkat." />}
              {devices.map((d) => (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-imp-dev-row-${d.id}`}>
                  <td className="px-3 py-2 font-semibold">{d.name}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{d.device_code}</td>
                  <td className="px-3 py-2 text-[10px]">{[d.brand, d.model].filter(Boolean).join(" ") || "—"}</td>
                  <td className="px-3 py-2 text-[10px] uppercase tracking-wider">{AUTH_TECH.find((t) => t.value === d.auth_technology)?.label || d.auth_technology}</td>
                  <td className="px-3 py-2 text-[10px]">{d.timezone}</td>
                  <td className="px-3 py-2 text-[10px]">{d.is_active ? <span className="text-emerald-700 font-semibold uppercase tracking-wider">Aktif</span> : <span className="text-slate-400 uppercase tracking-wider">Nonaktif</span>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canWrite && (<>
                      <button type="button" onClick={() => setEditDev({ ...emptyDev, ...d, location_id: d.location_id || "" })} data-testid={`att-imp-dev-edit-${d.id}`} className="p-1 text-slate-500 hover:text-[#0B1D3A]"><Pencil size={12} /></button>
                      {d.is_active && <button type="button" onClick={() => delDev(d.id)} data-testid={`att-imp-dev-del-${d.id}`} className="p-1 text-rose-500 hover:text-rose-700"><Trash2 size={12} /></button>}
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editDev && (
          <div className="border border-[#0B1D3A] bg-white p-4 space-y-2" data-testid="att-imp-dev-form">
            <div className="font-display text-sm text-[#0B1D3A]">{editDev.id ? "Edit Perangkat" : "Tambah Perangkat"}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <Input placeholder="Nama perangkat *" value={editDev.name} onChange={(e) => setEditDev({ ...editDev, name: e.target.value })} data-testid="att-imp-dev-name" />
              <Input placeholder="ID perangkat fisik * (mis. SN mesin)" value={editDev.device_code} onChange={(e) => setEditDev({ ...editDev, device_code: e.target.value })} data-testid="att-imp-dev-code" />
              <SearchableSelect value={editDev.auth_technology} onChange={(v) => setEditDev({ ...editDev, auth_technology: v })} options={AUTH_TECH} placeholder="Teknologi" testid="att-imp-dev-tech" />
              <Input placeholder="Merk (mis. Solution, FingerTec)" value={editDev.brand || ""} onChange={(e) => setEditDev({ ...editDev, brand: e.target.value })} data-testid="att-imp-dev-brand" />
              <Input placeholder="Model" value={editDev.model || ""} onChange={(e) => setEditDev({ ...editDev, model: e.target.value })} />
              <Input placeholder="Zona Waktu (Asia/Jakarta)" value={editDev.timezone} onChange={(e) => setEditDev({ ...editDev, timezone: e.target.value })} />
              <SearchableSelect value={editDev.location_id} onChange={(v) => setEditDev({ ...editDev, location_id: v })}
                options={[{ value: "", label: "— Tanpa lokasi —" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
                placeholder="Lokasi perangkat…" searchPlaceholder="Cari lokasi…" emptyText="Lokasi tidak ditemukan" testid="att-imp-dev-loc" />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={saveDev} disabled={!editDev.name || !editDev.device_code} className="rounded-none bg-[#0B1D3A]" data-testid="att-imp-dev-save"><Save size={12} /> Simpan</Button>
              <Button size="sm" variant="outline" onClick={() => setEditDev(null)} className="rounded-none" data-testid="att-imp-dev-cancel"><XCircle size={12} /> Batal</Button>
            </div>
          </div>
        )}
      </div>

      {/* Sumber */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A]">Sumber Data (Template Mesin / Software)</h3>
          {canWrite && <Button size="sm" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" onClick={() => setEditSrc(emptySrc)} data-testid="att-imp-src-add"><Plus size={14} /> Tambah Sumber</Button>}
        </div>
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr>
              <Th>Nama</Th><Th>Jenis</Th><Th>Ruang Lingkup ID</Th><Th>Perangkat</Th><Th right>Impor</Th><Th>Impor Terakhir</Th><Th right>Aksi</Th>
            </tr></thead>
            <tbody>
              {sources.length === 0 && <EmptyRow cols={7} text="Belum ada sumber." />}
              {sources.map((s) => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-imp-src-row-${s.id}`}>
                  <td className="px-3 py-2 font-semibold">{s.name}{!s.is_active && <span className="ml-2 text-[9px] uppercase tracking-wider text-slate-400">Nonaktif</span>}
                    {s.mapping_profile && <span className="ml-2 text-[9px] uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-200 px-1 py-0.5">Profil tersimpan</span>}</td>
                  <td className="px-3 py-2 text-[10px]">{s.source_type === "device_file" ? "File Mesin" : "File Software"}</td>
                  <td className="px-3 py-2 text-[10px] uppercase tracking-wider">{s.scope === "device" ? "Per Perangkat" : "Per Software"}</td>
                  <td className="px-3 py-2 text-[10px]">{(s.device_ids || []).map((id) => devices.find((d) => d.id === id)?.name || "?").join(", ") || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.import_count ?? 0}×</td>
                  <td className="px-3 py-2 tabular-nums text-[10px]">{fmtDT(s.last_import_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {canWrite && <button type="button" onClick={() => setEditSrc({ ...emptySrc, ...s, timezone: s.timezone || "" })} data-testid={`att-imp-src-edit-${s.id}`} className="p-1 text-slate-500 hover:text-[#0B1D3A]"><Pencil size={12} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editSrc && (
          <div className="border border-[#0B1D3A] bg-white p-4 space-y-2" data-testid="att-imp-src-form">
            <div className="font-display text-sm text-[#0B1D3A]">{editSrc.id ? "Edit Sumber" : "Tambah Sumber"}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <Input placeholder="Nama sumber * (mis. Mesin X100 Kantor)" value={editSrc.name} onChange={(e) => setEditSrc({ ...editSrc, name: e.target.value })} data-testid="att-imp-src-name" />
              <SearchableSelect value={editSrc.source_type} onChange={(v) => setEditSrc({ ...editSrc, source_type: v })}
                options={[{ value: "device_file", label: "File Mesin (CSV/XLSX)" }, { value: "software_file", label: "File Software Absensi" }]}
                placeholder="Jenis" testid="att-imp-src-type" />
              <SearchableSelect value={editSrc.scope} onChange={(v) => setEditSrc({ ...editSrc, scope: v })}
                options={[{ value: "device", label: "ID unik per perangkat" }, { value: "software", label: "ID unik per software" }]}
                placeholder="Ruang lingkup" testid="att-imp-src-scope" />
              <Input placeholder="Zona waktu override (opsional)" value={editSrc.timezone} onChange={(e) => setEditSrc({ ...editSrc, timezone: e.target.value })} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Perangkat tercakup</div>
              <div className="flex flex-wrap gap-3" data-testid="att-imp-src-devices">
                {devices.filter((d) => d.is_active).map((d) => (
                  <label key={d.id} className="inline-flex items-center gap-1.5 text-[11px]" data-testid={`att-imp-src-dev-${d.id}`}>
                    <input type="checkbox" checked={(editSrc.device_ids || []).includes(d.id)}
                      onChange={(e) => setEditSrc({ ...editSrc, device_ids: e.target.checked ? [...(editSrc.device_ids || []), d.id] : (editSrc.device_ids || []).filter((x) => x !== d.id) })} />
                    {d.name}
                  </label>
                ))}
                {devices.filter((d) => d.is_active).length === 0 && <span className="text-[10px] text-slate-400 italic">Belum ada perangkat aktif.</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={saveSrc} disabled={!editSrc.name} className="rounded-none bg-[#0B1D3A]" data-testid="att-imp-src-save"><Save size={12} /> Simpan</Button>
              <Button size="sm" variant="outline" onClick={() => setEditSrc(null)} className="rounded-none" data-testid="att-imp-src-cancel"><XCircle size={12} /> Batal</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= Pemetaan ID ================= */
function IdentityMapPanel({ canWrite, sources, devices, users, reloadKey }) {
  const [rows, setRows] = useState(null);
  const [filterSrc, setFilterSrc] = useState("");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    const q = filterSrc ? `?source_id=${filterSrc}` : "";
    api.get(`/attendance/identity-maps${q}`).then((r) => setRows(r.data || [])).catch((e) => toast.error(formatDetail(e.response?.data?.detail)));
  }, [filterSrc]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const empty = { source_id: "", scope_id: "", external_person_id: "", user_id: "", effective_from: "" };
  const srcObj = sources.find((s) => s.id === editing?.source_id);
  const scopeOptions = srcObj?.scope === "device"
    ? devices.filter((d) => (srcObj.device_ids || []).includes(d.id)).map((d) => ({ value: d.id, label: d.name }))
    : editing ? [{ value: editing.source_id, label: "Sumber software (seluruh sumber)" }] : [];

  const save = async () => {
    try {
      const scopeId = srcObj?.scope === "software" ? editing.source_id : editing.scope_id;
      await api.post("/attendance/identity-maps", {
        source_id: editing.source_id, scope_id: scopeId,
        external_person_id: editing.external_person_id.trim(), user_id: editing.user_id,
        effective_from: editing.effective_from || null,
      });
      toast.success("Pemetaan tersimpan — pemetaan lama untuk ID yang sama otomatis ditutup (histori tetap ada).");
      setEditing(null); load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="att-imp-idmap">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A] flex items-center gap-2"><Link2 size={14} /> Pemetaan ID Mesin → Karyawan</h3>
        <div className="flex items-center gap-2">
          <div className="w-56">
            <SearchableSelect value={filterSrc} onChange={setFilterSrc}
              options={[{ value: "", label: "Semua sumber" }, ...sources.map((s) => ({ value: s.id, label: s.name }))]}
              placeholder="Filter sumber…" testid="att-imp-idmap-filter" />
          </div>
          {canWrite && <Button size="sm" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" onClick={() => setEditing(empty)} data-testid="att-imp-idmap-add"><Plus size={14} /> Tambah Pemetaan</Button>}
        </div>
      </div>
      <div className="text-[10px] text-slate-500 italic">
        ID person mesin tidak dianggap unik lintas mesin — pemetaan berlaku per ruang lingkup (perangkat/software) dan berlaku efektif sejak tanggal tertentu. ID tak dikenal saat impor dilewati, tidak membuat karyawan baru.
      </div>
      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr>
            <Th>Sumber</Th><Th>Ruang Lingkup</Th><Th>ID Mesin</Th><Th>Karyawan</Th><Th>Berlaku</Th><Th>Status</Th>
          </tr></thead>
          <tbody>
            {rows === null && <EmptyRow cols={6} text="Memuat pemetaan…" />}
            {rows?.length === 0 && <EmptyRow cols={6} text="Belum ada pemetaan." />}
            {rows?.map((m) => {
              const s = sources.find((x) => x.id === m.source_id);
              const scopeName = s?.scope === "device" ? (devices.find((d) => d.id === m.scope_id)?.name || m.scope_id?.slice(0, 8)) : "Seluruh software";
              return (
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-imp-idmap-row-${m.id}`}>
                  <td className="px-3 py-2">{s?.name || "—"}</td>
                  <td className="px-3 py-2 text-[10px]">{scopeName}</td>
                  <td className="px-3 py-2 font-mono text-[10px] font-semibold">{m.external_person_id}</td>
                  <td className="px-3 py-2 font-semibold">{m.user_name || m.user_id}</td>
                  <td className="px-3 py-2 text-[10px] tabular-nums">{m.effective_from} s/d {m.effective_to || "∞"}</td>
                  <td className="px-3 py-2 text-[10px]">{m.is_active ? <span className="text-emerald-700 font-semibold uppercase tracking-wider">Aktif</span> : <span className="text-slate-400 uppercase tracking-wider">Digantikan</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="border border-[#0B1D3A] bg-white p-4 space-y-2" data-testid="att-imp-idmap-form">
          <div className="font-display text-sm text-[#0B1D3A]">Tambah Pemetaan ID</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            <SearchableSelect value={editing.source_id} onChange={(v) => setEditing({ ...editing, source_id: v, scope_id: "" })}
              options={sources.filter((s) => s.is_active).map((s) => ({ value: s.id, label: s.name }))}
              placeholder="Sumber *" searchPlaceholder="Cari sumber…" emptyText="Sumber tidak ditemukan" testid="att-imp-idmap-source" />
            <SearchableSelect value={srcObj?.scope === "software" ? editing.source_id : editing.scope_id}
              onChange={(v) => setEditing({ ...editing, scope_id: v })}
              options={scopeOptions} disabled={!editing.source_id || srcObj?.scope === "software"}
              placeholder={srcObj?.scope === "device" ? "Perangkat *" : "Ruang lingkup software"}
              searchPlaceholder="Cari perangkat…" emptyText="Perangkat tidak ditemukan" testid="att-imp-idmap-scope" />
            <Input placeholder="ID person di mesin * (mis. 007)" value={editing.external_person_id} onChange={(e) => setEditing({ ...editing, external_person_id: e.target.value })} data-testid="att-imp-idmap-extid" />
            <SearchableSelect value={editing.user_id} onChange={(v) => setEditing({ ...editing, user_id: v })}
              options={users.map((u) => ({ value: u.id, label: `${u.name} — ${u.division || "—"}` }))}
              placeholder="Karyawan *" searchPlaceholder="Cari nama / divisi…" emptyText="Karyawan tidak ditemukan" testid="att-imp-idmap-user" />
            <Input placeholder="Berlaku dari (YYYY-MM-DD, opsional)" value={editing.effective_from} onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })} data-testid="att-imp-idmap-from" />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={!editing.source_id || !editing.external_person_id.trim() || !editing.user_id || (srcObj?.scope === "device" && !editing.scope_id)} className="rounded-none bg-[#0B1D3A]" data-testid="att-imp-idmap-save"><Save size={12} /> Simpan</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="rounded-none" data-testid="att-imp-idmap-cancel"><XCircle size={12} /> Batal</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= Panel utama ================= */
export default function ImporLogPanel({ meta }) {
  const [sub, setSub] = useState("unggah");
  const [sources, setSources] = useState([]);
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const canWrite = !!meta?.can_configure;
  const canReview = !!meta?.can_approve || canWrite;

  const reload = useCallback(async () => {
    try {
      const [s, d, l, u] = await Promise.all([
        api.get("/attendance/sources?include_inactive=true"),
        api.get("/attendance/devices?include_inactive=true"),
        api.get("/attendance/locations").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setSources(s.data || []); setDevices(d.data || []); setLocations(l.data || []); setUsers(u.data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { reload(); }, [reload, reloadKey]);
  const bump = () => setReloadKey((k) => k + 1);

  const SUBS = [
    ["unggah", "Impor Baru"],
    ["antrean", "Antrean Review"],
    ["riwayat", "Riwayat Impor"],
    ["perangkat", "Perangkat & Sumber"],
    ["pemetaan", "Pemetaan ID"],
  ];
  return (
    <div className="space-y-3" data-testid="att-imp-panel">
      <div className="border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-900 flex items-start gap-2" data-testid="att-imp-info">
        <FileSpreadsheet size={14} className="mt-0.5 shrink-0" />
        <span>
          Impor log dari mesin/software absensi (CSV/XLSX) bersifat <b>brand-agnostic</b> — petakan kolom sesuai template file Anda.
          Log mesin menjadi <b>bukti pendukung</b> dan tidak pernah menimpa absen website; konflik diputuskan HR di Antrean Review.
        </span>
      </div>
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {SUBS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setSub(k)} data-testid={`att-imp-sub-${k}`}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 whitespace-nowrap ${sub === k ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{l}</button>
        ))}
      </div>
      {sub === "unggah" && <WizardPanel sources={sources} devices={devices} canWrite={canWrite} onChanged={bump} />}
      {sub === "antrean" && <ReviewQueuePanel canReview={canReview} reloadKey={reloadKey} onChanged={bump} />}
      {sub === "riwayat" && <BatchHistoryPanel reloadKey={reloadKey} />}
      {sub === "perangkat" && <DevicesSourcesPanel canWrite={canWrite} sources={sources} devices={devices} locations={locations} reload={bump} />}
      {sub === "pemetaan" && <IdentityMapPanel canWrite={canWrite} sources={sources} devices={devices} users={users} reloadKey={reloadKey} />}
    </div>
  );
}
